import { Footer } from '@workspace/shared/components/Footer';
import { Typography } from '@workspace/ui/components/Typography';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useLocation, useMatch } from 'react-router-dom';

import { company } from '~/company/Company';
import { FooterCDNavLinks } from '~/components/Footer/FooterCD/FooterCDNavLinks';
import { FooterSocialLinks } from '~/components/Footer/FooterSocialLinks';
import { FooterSupportButton } from '~/components/Footer/FooterSupportButton';
import { FooterMini } from '~/components/FooterMini';
import { HeaderV3 } from '~/components/HeaderV3';
import PageHeader from '~/components/isolated/PageHeader';
import { useFooterHeight } from '~/hooks/useFooterHeight';
import { useHeaderHeight } from '~/hooks/useHeaderHeight';
import { mainStore } from '~/stores';
import { heroBannerStore } from '~/stores/HeroBannerStore';

import FooterSaas from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import HeaderMini from '../../components/HeaderMini/HeaderMini';
import { CONTENT_ID, ROUTES } from './constants';
import styles from './DesktopLayout.module.scss';
import { getPageHeaderConfig } from './pageHeaderConfig';

interface DesktopLayoutProps {
  children: React.ReactElement;
  layoutRef: React.Ref<HTMLDivElement>;
}

const DesktopLayout = ({ children, layoutRef }: DesktopLayoutProps) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const matchOrderPayment = useMatch(ROUTES.order_payment);

  const headerRef = useRef<HTMLHeadingElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  const header = (() => {
    // Cart, Checkout and Cabinet pages use PageHeader
    const pageHeaderConfig = getPageHeaderConfig(pathname, false, t);
    if (company.newDesignSystemEnabled && pageHeaderConfig) {
      // Skip PageHeader on desktop if hideOnDesktop flag is set (use default header instead)
      if (!pageHeaderConfig.hideOnDesktop) {
        return <PageHeader {...pageHeaderConfig} ref={headerRef} />;
      }
    }

    if (!company.hasNewCheckoutElemtnts) {
      if (matchOrderPayment) {
        return <HeaderMini ref={headerRef} />;
      }
      return <Header ref={headerRef} />;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return company.newDesignSystemEnabled ? <HeaderV3 /> : <HeaderMini />;
      default:
        return <Header ref={headerRef} />;
    }
  })();

  const Variant1 = (
    <Footer.MCosmetic
      logoWidth={company.match({
        ocado: 'w-32',
        default: 'w-78',
      })}
      logo={{ desktop: company.images.logo.vector }}
      name={company.config.name}
    >
      <Footer.TopSection>
        <FooterCDNavLinks variant="light" className="flex-1" />
        <div className="flex w-[125px] flex-col items-center gap-3">
          <div className="h-[125px] rounded-6 bg-graphic-regular-white p-[10px]">
            <QRCode value="provide link please" size={105} offset={10} />
          </div>
          <Typography.Text200Medium className="text-center">
            {t('downloadTheApp')}
          </Typography.Text200Medium>
        </div>
      </Footer.TopSection>
      <Footer.Socials>
        <div className="grid grid-cols-[1fr_125px]">
          <FooterSocialLinks variant="light-outline" iconSize={20}>
            <FooterSupportButton className="-order-1 mr-2 inline-flex h-10 cursor-pointer items-center gap-[6px] rounded-full border-none bg-action-brand px-4 py-3 text-graphic-regular-white transition-colors duration-300 [font:var(--text-200-semibold)] hover:bg-action-brand-hover" />
          </FooterSocialLinks>
        </div>
      </Footer.Socials>
    </Footer.MCosmetic>
  );

  const footer = (() => {
    const footerComp = company.match({
      mcosmetic: Variant1,
      ocado: Variant1,
      default: <FooterSaas />,
    });

    if (!company.hasNewCheckoutElemtnts) {
      if (matchOrderPayment) {
        return <FooterMini ref={footerRef} />;
      }
      return footerComp;
    }

    switch (pathname) {
      case ROUTES.checkout:
        return <FooterMini />;
      default:
        return footerComp;
    }
  })();

  useHeaderHeight(headerRef, [pathname]);
  useFooterHeight(footerRef, [pathname]);

  return (
    <div
      className={classes([
        'page-layout',
        styles.desktopLayout,
        company.newDesignSystemEnabled && styles.redesign,
      ])}
      ref={layoutRef}
    >
      {header}

      {company.newDesignSystemEnabled ? (
        <div
          className={classes([
            styles.content,
            mainStore.isDeliveryBannerShown,
            heroBannerStore.shouldShowSection &&
              pathname === ROUTES.index &&
              styles.withHeroBanner,
          ])}
          id={CONTENT_ID}
        >
          {/* I DONT KNOW WHY, BUT IT EMPTY DIV FIXED HEADER CORNERS FOR WINDOW CHROME */}
          <div className={styles.mock} />
          {children}
          {footer}
        </div>
      ) : (
        <>
          {children}
          {footer}
        </>
      )}
    </div>
  );
};

export default observer(DesktopLayout);
